import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Bell, Settings, User, CreditCard, Mic,
  Menu, X, BarChart3, TrendingUp, Target as TargetIcon, Calendar, ChevronDown,
  LayoutDashboard, Tag, LogOut, CheckCheck, Trash2,
  AlertTriangle, Info, Pencil, Camera, Users, Lock,
} from "lucide-react";
import { useAppData } from "./store";
import { useAuth, hasPremiumPlan } from "../../../contexts/AuthContext";
import { toast } from "sonner";
import VoiceLancamentoModal from "./VoiceLancamentoModal";
import { brl } from "./utils";
import { useTheme } from "../../../hooks/useTheme";

const openVoice = () => window.dispatchEvent(new Event("monyx:open-voice"));
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const MESES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

const VISAO = [
  { to: "/app",                 icon: LayoutDashboard, label: "Painel" },
  { to: "/app/dashboard-anual", icon: BarChart3,       label: "Panorama Anual" },
  { to: "/app/investimentos",   icon: TrendingUp,      label: "Investimentos" },
  { to: "/app/cartoes",         icon: CreditCard,      label: "Cartões de Crédito" },
  { to: "/app/objetivos",       icon: TargetIcon,      label: "Planos e Metas" },
  { to: "/app/familia",         icon: Users,           label: "Família", premiumOnly: true },
  { to: "/app/categorias",      icon: Tag,             label: "Categorias" },
];

const LANC = [
  { to: "/app/entradas",          label: "Entradas" },
  { to: "/app/gastos-fixos",      label: "Gastos Fixos" },
  { to: "/app/gastos-variaveis",  label: "Gastos Variáveis" },
  { to: "/app/lista-de-compras",  label: "Lista de Compras" },
];

// ── Notifications hook ──────────────────────────────────────────
const NOTIF_READ_KEY      = "monyx_read_notifs_v1";
const NOTIF_DISMISSED_KEY = "monyx_dismissed_notifs_v1";

const useNotifications = () => {
  const { data } = useAppData();

  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(NOTIF_READ_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(NOTIF_DISMISSED_KEY) || "[]")); }
    catch { return new Set(); }
  });

  const allItems = useMemo(() => {
    const today = new Date();
    const items = [];

    // Card due-date alerts (≤7 days away)
    (data.cartoes || []).forEach((c) => {
      if (!c.vencimento) return;
      const dueDay = parseInt(c.vencimento, 10);
      let due = new Date(today.getFullYear(), today.getMonth(), dueDay);
      if (due < today) due.setMonth(due.getMonth() + 1);
      const days = Math.ceil((due - today) / 86400000);
      if (days >= 0 && days <= 7) {
        items.push({
          id: `card-due-${c.id}-${today.getFullYear()}-${today.getMonth()}`,
          type: days <= 2 ? "danger" : "warning",
          title: `Fatura ${c.nome || "Cartão"} vence ${days === 0 ? "hoje" : `em ${days} dia${days !== 1 ? "s" : ""}`}`,
          desc: `Vencimento: dia ${dueDay} de cada mês`,
          time: "Hoje",
        });
      }
    });

    // Spending limit alert
    const [y, mo] = (data.selectedMonth || getCurrentMonth()).split("-");
    const inMo = (arr) => (arr || []).filter((x) => (x.data || "").startsWith(`${y}-${mo}`));
    const sumV = (arr) => arr.reduce((s, x) => s + Number(x.valor || 0), 0);
    const gastos = sumV([
      ...inMo(data.gastosFixos || []),
      ...inMo(data.gastosVariaveis || []),
      ...(data.cartoes || []).flatMap((c) => inMo(c.transacoes || [])),
    ]);
    const entradas = sumV(inMo(data.entradas || []));
    if (entradas > 0) {
      const pct = Math.round((gastos / entradas) * 100);
      if (pct >= 60) {
        items.push({
          id: `spending-${data.selectedMonth}`,
          type: pct >= 90 ? "danger" : "warning",
          title: `Você atingiu ${pct}% do limite de gastos`,
          desc: `${brl(gastos)} gastos de ${brl(entradas)} em entradas`,
          time: "Este mês",
        });
      }
    }

    // Recent variable expenses (last 3 days)
    const recentV = (data.gastosVariaveis || []).filter((e) => {
      try { return (today - new Date(e.data)) < 3 * 86400000; } catch { return false; }
    });
    if (recentV.length > 0) {
      items.push({
        id: `recent-v-${recentV.length}-${recentV[0]?.id || ""}`,
        type: "info",
        title: `${recentV.length} gasto${recentV.length !== 1 ? "s variáveis" : " variável"} recente${recentV.length !== 1 ? "s" : ""}`,
        desc: recentV.slice(0, 2).map((e) => e.descricao || "Gasto").join(", "),
        time: "Últimos 3 dias",
      });
    }

    return items;
  }, [data]);

  const notifications = useMemo(
    () => allItems.filter((n) => !dismissedIds.has(n.id)),
    [allItems, dismissedIds]
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => !readIds.has(n.id)).length,
    [notifications, readIds]
  );

  const persistRead = (next) => {
    setReadIds(next);
    try { localStorage.setItem(NOTIF_READ_KEY, JSON.stringify([...next])); } catch {}
  };
  const persistDismissed = (next) => {
    setDismissedIds(next);
    try { localStorage.setItem(NOTIF_DISMISSED_KEY, JSON.stringify([...next])); } catch {}
  };

  const markRead = useCallback((id) => {
    persistRead(new Set([...readIds, id]));
  }, [readIds]); // eslint-disable-line

  const markAllRead = useCallback(() => {
    persistRead(new Set([...readIds, ...notifications.map((n) => n.id)]));
  }, [notifications, readIds]); // eslint-disable-line

  const dismissNotif = useCallback((id) => {
    persistDismissed(new Set([...dismissedIds, id]));
    persistRead(new Set([...readIds, id]));
  }, [dismissedIds, readIds]); // eslint-disable-line

  const clearAll = useCallback(() => {
    const ids = allItems.map((n) => n.id);
    persistDismissed(new Set([...dismissedIds, ...ids]));
    persistRead(new Set([...readIds, ...ids]));
  }, [allItems, dismissedIds, readIds]); // eslint-disable-line

  return { notifications, unreadCount, markAllRead, markRead, dismissNotif, clearAll, readIds };
};

// ── Notifications Panel ─────────────────────────────────────────
const NotifTypeIcon = ({ type }) => {
  if (type === "danger")  return <AlertTriangle className="h-4 w-4 text-rose-400" />;
  if (type === "warning") return <AlertTriangle className="h-4 w-4 text-amber-400" />;
  return <Info className="h-4 w-4 text-blue-400" />;
};

const NotificationsPanel = ({ open, onClose, notifications, unreadCount, markAllRead, markRead, dismissNotif, clearAll, readIds }) => (
  <>
    {/* Overlay escuro abaixo da topbar */}
    {open && (
      <button aria-label="fechar" onClick={onClose}
        className="fixed inset-x-0 bottom-0 z-[45] bg-black/60 sm:hidden" style={{ top: 64 }} />
    )}
    <aside className={`fixed inset-x-0 w-full sm:inset-x-auto sm:right-0 sm:w-80 sm:border-l z-[50] bg-slate-950 border-slate-800 flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0 translate-y-0" : "max-sm:translate-y-full sm:translate-x-full sm:translate-y-0"}`}
      style={{ top: 64, height: "calc(100vh - 64px)" }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-white text-sm">Notificações</div>
          {unreadCount > 0 && (
            <span className="h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
              {unreadCount}
            </span>
          )}
        </div>
        <button onClick={onClose}
          className="h-8 w-8 grid place-items-center text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1 bg-slate-950">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-2">
            <Bell className="h-8 w-8 opacity-30" />
            <div className="text-sm">Tudo em dia por aqui</div>
          </div>
        ) : (
          <div className="p-3 space-y-1.5">
            {notifications.map((n) => {
              const unread = !readIds.has(n.id);
              return (
                <div key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`group relative rounded-xl px-3 py-3 border cursor-pointer transition-all ${
                    unread
                      ? "bg-slate-900 border-slate-700 hover:border-slate-600"
                      : "bg-slate-900/30 border-slate-800/50 hover:border-slate-700/60"
                  }`}>
                  <div className="flex items-start gap-2.5 pr-6">
                    {/* Type icon */}
                    <div className={`mt-0.5 h-7 w-7 rounded-lg grid place-items-center shrink-0 ${
                      n.type === "danger" ? "bg-rose-500/15" : n.type === "warning" ? "bg-amber-500/15" : "bg-blue-500/15"
                    }`}>
                      <NotifTypeIcon type={n.type} />
                    </div>
                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {unread && <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />}
                        <div className={`text-sm font-medium leading-snug truncate ${unread ? "text-white" : "text-slate-400"}`}>
                          {n.title}
                        </div>
                      </div>
                      {n.desc && <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.desc}</div>}
                      <div className="text-[10px] text-slate-600 mt-1">{n.time}</div>
                    </div>
                  </div>
                  {/* Dismiss button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); dismissNotif(n.id); }}
                    className="absolute top-2.5 right-2.5 h-5 w-5 grid place-items-center rounded text-slate-600 hover:text-slate-300 hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-slate-800 p-3 flex gap-2">
        <button
          onClick={markAllRead}
          disabled={unreadCount === 0}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/10 transition disabled:opacity-30 disabled:cursor-not-allowed">
          <CheckCheck className="h-3.5 w-3.5" /> Marcar lidas
        </button>
        <button
          onClick={clearAll}
          disabled={notifications.length === 0}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-slate-400 border border-slate-700 hover:bg-slate-800 hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed">
          <Trash2 className="h-3.5 w-3.5" /> Limpar todas
        </button>
      </div>
    </aside>
  </>
);

// ── Shared toggle switch ────────────────────────────────────────
const ToggleSwitch = ({ checked, onChange }) => (
  <button type="button" onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${checked ? "bg-emerald-500" : "bg-slate-700"}`}>
    <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
      style={{ transform: checked ? "translateX(22px)" : "translateX(2px)" }} />
  </button>
);

// ── Settings Dropdown ───────────────────────────────────────────
const SettingsDropdown = ({ onClose }) => {
  const auth = useAuth();
  const { data } = useAppData();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [editName, setEditName] = useState(auth?.user?.name || "");
  const [notifOn, setNotifOn] = useState(() => {
    try { return JSON.parse(localStorage.getItem("monyx_notif_prefs") || "{}").all !== false; }
    catch { return true; }
  });
  const [showDelete, setShowDelete] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `monyx-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Dados exportados!");
  };

  const doDelete = async () => {
    if (delConfirm !== "EXCLUIR") return;
    await auth.logout();
    navigate("/login");
  };

  return (
    <div className="fixed right-2 z-[50] w-[calc(100vw-16px)] sm:w-80 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-80px)] flex flex-col"
      style={{ top: 68 }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2">
          <Settings className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-sm font-semibold text-white">Configurações</span>
        </div>
        <button onClick={onClose} className="h-7 w-7 grid place-items-center text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 p-4 space-y-1">
        {/* Nome */}
        <div className="pb-3 border-b border-slate-800/60">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nome</div>
          <div className="flex gap-2">
            <input value={editName} onChange={(e) => setEditName(e.target.value)}
              className="flex-1 h-9 rounded-lg bg-slate-900 border border-slate-700 px-3 text-sm text-white focus:outline-none focus:border-emerald-500/50 min-w-0" />
            <button onClick={() => toast.success("Nome atualizado!")}
              className="px-3 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/25 transition shrink-0">Salvar</button>
          </div>
        </div>
        {/* Email */}
        <div className="py-3 border-b border-slate-800/60">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email</div>
          <input value={auth?.user?.email || ""} readOnly
            className="w-full h-9 rounded-lg bg-slate-800/40 border border-slate-700 px-3 text-sm text-slate-500 cursor-not-allowed" />
          <p className="mt-1 text-[10px] text-slate-600">Vinculado à conta — não editável</p>
        </div>
        {/* Toggles */}
        <div className="flex items-center justify-between py-2.5 border-b border-slate-800/60">
          <span className="text-sm text-slate-300">Modo Dark</span>
          <ToggleSwitch checked={isDark} onChange={toggleTheme} />
        </div>
        <div className="flex items-center justify-between py-2.5 border-b border-slate-800/60">
          <span className="text-sm text-slate-300">Notificações</span>
          <ToggleSwitch checked={notifOn} onChange={() => setNotifOn((v) => !v)} />
        </div>
        {/* Exportar */}
        <div className="py-2.5 border-b border-slate-800/60">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Exportar dados</div>
          <div className="flex gap-2">
            {[
              { fmt: "JSON",  fn: exportJSON },
              { fmt: "Excel", fn: () => toast.info("Em breve") },
              { fmt: "PDF",   fn: () => toast.info("Em breve") },
            ].map(({ fmt, fn }) => (
              <button key={fmt} onClick={fn}
                className="flex-1 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-slate-300 hover:border-emerald-500/30 hover:text-emerald-300 transition">{fmt}</button>
            ))}
          </div>
        </div>
        {/* Excluir conta */}
        <div className="pt-2">
          {!showDelete ? (
            <button onClick={() => setShowDelete(true)}
              className="flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300 transition w-full py-1">
              <Trash2 className="h-3.5 w-3.5" /> Excluir conta
            </button>
          ) : (
            <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3 space-y-2">
              <div className="text-xs text-rose-300 font-semibold">Esta ação é irreversível</div>
              <div className="text-[11px] text-slate-400">Digite <span className="font-mono font-bold text-rose-400">EXCLUIR</span></div>
              <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="EXCLUIR"
                className="w-full bg-slate-950 border border-rose-500/30 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-rose-500" />
              <div className="flex gap-2">
                <button onClick={() => { setShowDelete(false); setDelConfirm(""); }}
                  className="flex-1 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:bg-slate-800 transition">Cancelar</button>
                <button onClick={doDelete} disabled={delConfirm !== "EXCLUIR"}
                  className="flex-1 py-1.5 rounded-lg bg-rose-600 text-xs text-white font-semibold hover:bg-rose-500 transition disabled:opacity-40 disabled:cursor-not-allowed">Excluir</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Profile Dropdown ────────────────────────────────────────────
const ProfileDropdown = ({ onClose }) => {
  const auth = useAuth();
  const navigate = useNavigate();

  const [editName,     setEditName]     = useState(auth?.user?.name  || "");
  const [editEmail,    setEditEmail]    = useState(auth?.user?.email || "");
  const [editingName,  setEditingName]  = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [photo,        setPhoto]        = useState(null);

  const nameDirty  = editName  !== (auth?.user?.name  || "");
  const emailDirty = editEmail !== (auth?.user?.email || "");
  const isDirty    = nameDirty || emailDirty || !!photo;

  const memberSince = (() => {
    const raw = auth?.user?.created_at;
    if (!raw) return "—";
    try {
      return new Date(raw).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    } catch { return "—"; }
  })();

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    toast.success("Perfil atualizado!");
    setEditingName(false);
    setEditingEmail(false);
  };

  const doLogout = async () => {
    onClose();
    await auth.logout();
    toast.success("Saiu da conta");
    navigate("/login");
  };

  return (
    <div className="fixed right-2 z-[50] w-[calc(100vw-16px)] sm:w-72 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden"
      style={{ top: 68 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-sm font-semibold text-white">Meu Perfil</span>
        </div>
        <button onClick={onClose} className="h-7 w-7 grid place-items-center text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Avatar com upload */}
        <div className="flex justify-center pt-1 pb-2">
          <label className="relative cursor-pointer group">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 grid place-items-center text-2xl font-bold text-white overflow-hidden ring-2 ring-slate-800">
              {photo
                ? <img src={photo} alt="avatar" className="h-full w-full object-cover" />
                : (auth?.user?.name || "?").slice(0, 1).toUpperCase()
              }
            </div>
            <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition grid place-items-center">
              <Camera className="h-5 w-5 text-white" />
            </div>
            <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={handlePhoto} />
          </label>
        </div>

        {/* Nome */}
        <div>
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome</div>
          <div className="flex items-center gap-1.5">
            {editingName ? (
              <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                className="flex-1 h-8 rounded-lg bg-slate-900 border border-emerald-500/50 px-2.5 text-sm text-white focus:outline-none min-w-0" />
            ) : (
              <span className="flex-1 text-sm text-white truncate">{editName || "—"}</span>
            )}
            <button onClick={() => setEditingName((v) => !v)}
              className={`h-6 w-6 grid place-items-center rounded transition shrink-0 ${editingName ? "text-emerald-400 bg-emerald-500/10" : "text-slate-500 hover:text-emerald-300"}`}>
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Email */}
        <div>
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Email</div>
          <div className="flex items-center gap-1.5">
            {editingEmail ? (
              <input autoFocus value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
                className="flex-1 h-8 rounded-lg bg-slate-900 border border-emerald-500/50 px-2.5 text-sm text-white focus:outline-none min-w-0" />
            ) : (
              <span className="flex-1 text-xs text-slate-400 truncate">{editEmail || "—"}</span>
            )}
            <button onClick={() => setEditingEmail((v) => !v)}
              className={`h-6 w-6 grid place-items-center rounded transition shrink-0 ${editingEmail ? "text-emerald-400 bg-emerald-500/10" : "text-slate-500 hover:text-emerald-300"}`}>
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Botão salvar — só aparece quando tem mudança */}
        {isDirty && (
          <button onClick={handleSave}
            className="w-full py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/25 transition">
            Salvar alterações
          </button>
        )}

        <div className="border-t border-slate-800" />

        {/* Membro desde */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Membro desde</span>
          <span className="text-xs text-slate-400 capitalize">{memberSince}</span>
        </div>

        <div className="border-t border-slate-800" />

        {/* Logout */}
        <button onClick={doLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 text-sm font-medium transition">
          <LogOut className="h-4 w-4" /> Sair da conta
        </button>
      </div>
    </div>
  );
};

// ── Icon Button ─────────────────────────────────────────────────
export const IconBtn = ({ children, onClick, testid, hideOnMobile = false }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`${hideOnMobile ? "hidden md:grid" : "grid"} h-9 w-9 place-items-center rounded-full border border-slate-800 bg-slate-900/60 text-slate-300 hover:text-emerald-300 hover:border-emerald-500/40 transition`}>
    {children}
  </button>
);

// ── Topbar Actions ──────────────────────────────────────────────
const TopbarActions = () => {
  const [openPanel, setOpenPanel] = useState(null); // "notif" | "settings" | "profile" | null
  const navigate = useNavigate();
  const notif = useNotifications();

  const toggle = (panel) => setOpenPanel((p) => (p === panel ? null : panel));

  return (
    <>
      {/* Botões do header */}
      <div className="flex items-center gap-2">
        {/* Mic */}
        <button
          data-testid="hdr-mic"
          onClick={openVoice}
          title="Lançar por voz"
          className="h-9 w-9 grid place-items-center rounded-full text-white shadow-md shadow-emerald-500/30 monyx-btn-primary hover:scale-110 transition-transform">
          <Mic className="h-4 w-4" />
        </button>

        {/* Bell with unread badge */}
        <div className="relative">
          <button
            data-testid="hdr-bell"
            onClick={() => toggle("notif")}
            className="h-9 w-9 grid place-items-center rounded-full border border-slate-800 bg-slate-900/60 text-slate-300 hover:text-emerald-300 hover:border-emerald-500/40 transition">
            <Bell className="h-4 w-4" />
          </button>
          {notif.unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center pointer-events-none">
              {notif.unreadCount > 9 ? "9+" : notif.unreadCount}
            </span>
          )}
        </div>

        <IconBtn testid="hdr-settings" onClick={() => toggle("settings")}>
          <Settings className="h-4 w-4" />
        </IconBtn>
        <IconBtn testid="hdr-user" onClick={() => toggle("profile")}>
          <User className="h-4 w-4" />
        </IconBtn>
      </div>

      {/* Portais — renderizados direto no document.body para escapar do backdrop-filter do header */}
      {createPortal(
        <>
          {openPanel && (
            <button aria-label="fechar" onClick={() => setOpenPanel(null)}
              className="fixed inset-x-0 bottom-0 z-[45] bg-black/60 sm:bg-transparent" style={{ top: 64 }} />
          )}
          <NotificationsPanel
            open={openPanel === "notif"}
            onClose={() => setOpenPanel(null)}
            notifications={notif.notifications}
            unreadCount={notif.unreadCount}
            markAllRead={notif.markAllRead}
            markRead={notif.markRead}
            dismissNotif={notif.dismissNotif}
            clearAll={notif.clearAll}
            readIds={notif.readIds}
          />
          {openPanel === "settings" && <SettingsDropdown onClose={() => setOpenPanel(null)} />}
          {openPanel === "profile"  && <ProfileDropdown  onClose={() => setOpenPanel(null)} />}
        </>,
        document.body
      )}
    </>
  );
};

// ── Sidebar ─────────────────────────────────────────────────────
const YEAR_START = 2024;
const YEAR_END   = 2050;
const ALL_YEARS  = Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, k) => YEAR_START + k);

export const Sidebar = ({ open, onClose }) => {
  const { data, setSelectedMonth } = useAppData();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = useState(() => !document.documentElement.classList.contains("light"));
  useEffect(() => {
    const update = () => setIsDark(!document.documentElement.classList.contains("light"));
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const [year, monthIdx] = (data.selectedMonth || getCurrentMonth()).split("-");
  const currentMonth = parseInt(monthIdx, 10) - 1;

  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const yearPickerRef = useRef(null);

  // Auto-scroll to selected year when picker opens
  useEffect(() => {
    if (yearPickerOpen && yearPickerRef.current) {
      const idx = parseInt(year, 10) - YEAR_START;
      yearPickerRef.current.scrollTop = Math.max(0, idx - 2) * 36;
    }
  }, [yearPickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Years that have at least one entry (shows green dot indicator)
  const yearsWithData = useMemo(() => {
    const set = new Set();
    const add = (arr) => (arr || []).forEach((x) => {
      const y = (x.data || "").slice(0, 4);
      if (y) set.add(parseInt(y, 10));
    });
    add(data.gastosFixos);
    add(data.gastosVariaveis);
    add(data.entradas);
    (data.cartoes || []).forEach((c) => add(c.transacoes || []));
    return set;
  }, [data]);

  const selectYear = (newYear) => {
    setSelectedMonth(`${newYear}-${monthIdx}`);
    setYearPickerOpen(false);
  };

  const selectMonth = (i) => {
    setSelectedMonth(`${year}-${String(i + 1).padStart(2, "0")}`);
    setYearPickerOpen(false);
    navigate("/app");
    onClose?.();
  };

  const doLogout = async () => {
    await auth.logout();
    toast.success("Saiu da conta");
    navigate("/login");
  };

  return (
    <>
      {open && (
        <button aria-label="fechar" onClick={onClose}
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" />
      )}
      <aside
        className={`fixed md:sticky top-0 left-0 z-50 md:z-10 h-screen w-64 shrink-0 transform transition-transform md:transform-none overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"} ${isDark ? "bg-slate-950 border-r border-slate-800" : "border-transparent text-white"}`}
        style={!isDark ? { background: 'linear-gradient(180deg, #00c853 0%, #2196f3 100%)' } : {}}
        data-testid="sidebar">
        <div className={`flex items-center justify-between md:justify-start gap-2 px-5 h-16 border-b ${isDark ? "border-slate-800" : "border-white/20"}`}>
          <Link to="/" className="flex items-center gap-2" onClick={onClose}>
            <img src="/monyx-bot-hero.png" alt="Monyxx" className="h-8 w-8 object-contain" />
            <div className="leading-none">
              <div className="font-display text-lg font-extrabold">
                <span className={isDark ? "text-emerald-400" : "text-white"}>M</span><span className="text-white">ony</span><span className={isDark ? "text-blue-400" : "text-white"}>xx</span>
              </div>
              <div className={`text-[8px] tracking-[0.3em] font-bold ${isDark ? "text-emerald-400/70" : "text-white/80"}`}>FINANCE</div>
            </div>
          </Link>
          <button onClick={onClose} className={`md:hidden h-8 w-8 grid place-items-center ${isDark ? "text-slate-300" : "text-white"}`}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="p-4 space-y-5">
          <div>
            <div className={`text-[10px] tracking-[0.2em] font-bold mb-2 px-2 ${isDark ? "text-blue-400" : "text-white/70"}`}>VISÃO GERAL</div>
            <div className="space-y-1">
              {VISAO.map((it) => {
                if (it.premiumOnly && !hasPremiumPlan(auth?.user)) return null;
                const active = location.pathname === it.to;
                const Icon = it.icon;
                
                const appliedClass = isDark 
                  ? (active ? "bg-blue-500/15 text-white border-l-2 border-blue-400" : "text-slate-300 hover:bg-slate-900 hover:text-white")
                  : (active ? "bg-white/20 text-white font-bold border-l-2 border-white" : "text-white hover:bg-white/10");

                return (
                  <Link key={it.to} to={it.to} onClick={onClose}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${appliedClass}`}
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{it.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div>
            <div className={`text-[10px] tracking-[0.2em] font-bold mb-2 px-2 ${isDark ? "text-emerald-400" : "text-white/70"}`}>LANÇAMENTOS</div>
            <div className="space-y-1">
              {LANC.map((it) => {
                const active = location.pathname === it.to;
                
                const appliedClass = isDark 
                  ? (active ? "bg-emerald-500/15 text-white border-l-2 border-emerald-400" : "text-slate-300 hover:bg-slate-900 hover:text-white")
                  : (active ? "bg-white/20 text-white font-bold border-l-2 border-white" : "text-white hover:bg-white/10");

                return (
                  <Link key={it.to} to={it.to} onClick={onClose}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${appliedClass}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${isDark ? "bg-emerald-400/80" : "bg-white"}`} />
                    <span className="truncate">{it.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div>
            {/* Year selector header */}
            <div className={`text-[10px] tracking-[0.2em] font-bold mb-2 px-2 flex items-center justify-between ${isDark ? "text-violet-400" : "text-white/70"}`}>
              MESES
              <button
                onClick={() => setYearPickerOpen((v) => !v)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-md transition cursor-pointer ${isDark ? "text-slate-300 hover:text-emerald-300 hover:bg-slate-800" : "text-white hover:bg-white/20"}`}
                aria-label="Selecionar ano"
                data-testid="year-picker-btn"
              >
                <span className="font-bold text-[11px]">{year}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${yearPickerOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            {/* Year dropdown — inline accordion */}
            {yearPickerOpen && (
              <div
                ref={yearPickerRef}
                className={`mb-2 rounded-xl overflow-y-auto shadow-lg ${isDark ? "bg-slate-900 border border-violet-500/20" : "bg-black/20 backdrop-blur-md border border-white/20"}`}
                style={{ maxHeight: 180 }}
                data-testid="year-picker-list"
              >
                {ALL_YEARS.map((y) => {
                  const isSelected = String(y) === year;
                  const hasData    = yearsWithData.has(y);
                  
                  const appliedClass = isDark
                    ? (isSelected ? "bg-emerald-500/15 text-emerald-300 font-semibold" : "text-slate-400 hover:bg-slate-800 hover:text-white")
                    : (isSelected ? "bg-white/30 text-white font-bold" : "text-white/90 hover:bg-white/10 hover:text-white");

                  return (
                    <button
                      key={y}
                      onClick={() => selectYear(y)}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm transition ${appliedClass}`}
                      data-testid={`year-${y}`}
                    >
                      <span>{y}</span>
                      {hasData && (
                        <span className={`h-1.5 w-1.5 rounded-full ${isDark ? (isSelected ? "bg-emerald-400" : "bg-emerald-600") : (isSelected ? "bg-white" : "bg-white/60")}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Month list */}
            <div className="space-y-0.5">
              {MESES.map((m, i) => {
                const active = i === currentMonth && location.pathname === "/app";
                
                const appliedClass = isDark
                  ? (active ? "bg-blue-500/15 text-white border-l-2 border-blue-400" : "text-slate-400 hover:bg-slate-900 hover:text-white")
                  : (active ? "bg-white/20 text-white font-bold border-l-2 border-white" : "text-white hover:bg-white/10");

                return (
                  <button key={m} onClick={() => selectMonth(i)}
                    className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition text-left ${appliedClass}`}
                    data-testid={`mes-${i + 1}`}>
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{m}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`pt-4 border-t ${isDark ? "border-slate-800" : "border-white/20"}`}>
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 grid place-items-center text-sm font-bold text-white shadow-md">
                {(auth?.user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-white"}`}>{auth?.user?.name}</div>
                <div className={`text-[10px] truncate ${isDark ? "text-slate-400" : "text-white/80"}`}>{auth?.user?.email}</div>
              </div>
            </div>
            <button onClick={doLogout}
              className={`mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition font-medium ${isDark ? "text-rose-300 hover:bg-rose-500/10" : "text-rose-100 bg-white/20 hover:bg-white/30"}`}
              data-testid="btn-logout">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

// ── App Layout ──────────────────────────────────────────────────
export const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { data } = useAppData();
  const location = useLocation();

  useEffect(() => {
    const onOpen = () => setVoiceOpen(true);
    window.addEventListener("monyx:open-voice", onOpen);
    return () => window.removeEventListener("monyx:open-voice", onOpen);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex notranslate overflow-x-hidden" translate="no">
      <div className="fixed inset-0 grid-pattern opacity-40 pointer-events-none" />
      <span className="glow-green fixed -top-40 -left-40 pointer-events-none" />
      <span className="glow-blue fixed -bottom-40 -right-40 pointer-events-none" />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 min-w-0 relative">
        {/* Hamburger: só no dashboard (sub-páginas têm botão voltar no header) */}
        <button
          onClick={() => setSidebarOpen(true)}
          className={`${location.pathname === "/app" ? "md:hidden" : "hidden"} fixed top-3 left-3 z-30 h-10 w-10 grid place-items-center rounded-lg bg-slate-900 border border-slate-800 text-white`}
          data-testid="open-sidebar">
          <Menu className="h-5 w-5" />
        </button>
        {children}
        <button
          onClick={() => setVoiceOpen(true)}
          data-testid="voice-fab"
          title="Lançar por voz"
          className="fixed bottom-24 right-6 md:bottom-8 md:right-8 z-50 h-14 w-14 grid place-items-center rounded-full text-white shadow-2xl shadow-emerald-500/30 monyx-btn-primary hover:scale-110 transition-transform">
          <Mic className="h-6 w-6" />
        </button>
        <VoiceLancamentoModal
          open={voiceOpen}
          onOpenChange={setVoiceOpen}
          defaultMonth={data?.selectedMonth}
        />
      </div>
    </div>
  );
};

// ── Page Header ─────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, right, onBack, testid }) => {
  const navigate = useNavigate();
  const showBack = onBack !== false;
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800">
      <div className="px-4 md:px-8 h-16 flex items-center gap-3">
        {showBack && (
          <button
            onClick={() => (onBack ? onBack() : navigate("/app"))}
            className="inline-flex items-center justify-center h-9 w-9 md:w-auto md:px-4 md:py-1.5 rounded-full text-sm font-semibold text-white shadow-md shadow-emerald-500/30 monyx-btn-primary hover:scale-105 transition-transform shrink-0"
            data-testid={testid ? `${testid}-back` : "back-btn"}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden md:inline ml-1.5">Voltar</span>
          </button>
        )}
        <div className={`min-w-0 flex-1 ${showBack ? "" : "ml-12 md:ml-0"}`}>
          <div className="font-display font-bold text-base md:text-lg truncate">{title}</div>
          {subtitle && <div className="text-xs text-slate-400 truncate">{subtitle}</div>}
        </div>
        {right ?? <TopbarActions />}
      </div>
    </header>
  );
};

// ── Dashboard Topbar ────────────────────────────────────────────
export const DashboardTopbar = () => {
  const { data, setSelectedMonth } = useAppData();
  const [year, monthIdx] = (data.selectedMonth || getCurrentMonth()).split("-");
  const i = parseInt(monthIdx, 10) - 1;

  const change = (delta) => {
    let m = i + delta, y = parseInt(year, 10);
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setSelectedMonth(`${y}-${String(m + 1).padStart(2, "0")}`);
  };

  return (
    <header className="sticky top-0 z-20 bg-slate-950 border-b border-slate-800">
      <div className="px-5 md:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 ml-12 md:ml-0">
          <button onClick={() => change(-1)}
            className="h-8 w-8 grid place-items-center rounded-lg border border-slate-700 hover:border-emerald-500/40 hover:text-emerald-300"
            data-testid="prev-month">‹</button>
          <div className="text-sm font-semibold capitalize px-3">{MESES[i]} {year}</div>
          <button onClick={() => change(1)}
            className="h-8 w-8 grid place-items-center rounded-lg border border-slate-700 hover:border-emerald-500/40 hover:text-emerald-300"
            data-testid="next-month">›</button>
        </div>
        <TopbarActions />
      </div>
    </header>
  );
};
